import React from "react";
import "./HomeView.scss";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {history, Nullable} from "@data-force/app";
import {Button, Empty} from "antd";
import StoreState from "../../interfaces/store/storeState";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {ClearHomeDataAction, GetHomeDataAction} from "../../interfaces/store/actions/home";
import {clearHomeData, getHomeData} from "../../store/actions/home";
import {JobFilterType, JobType} from "../../enums/enums";
import Spinner from "../../components/common/Spinner/Spinner";
import HistoryParams from "../../interfaces/historyParams";
import {formatString, getHistoryParams} from "../../utils/common";
import {
    getPhaseConfigList, setActiveJobType, setJobFilterType, setSelectedJob, showJobDrawer,
} from "../../store/actions/job";
import {
    GetPhaseConfigListAction,
    SetActiveJobTypeAction,
    SetJobFilterTypeAction,
    SetSelectedJobAction,
    ShowJobDrawerAction,
} from "../../interfaces/store/actions/job";
import {NoAnnouncementsIcon} from "../../assets/Icons";
import JobItem from "../../components/JobItem/JobItem";
import Announcement from "../../components/Announcement/Announcement";
import {AnnouncementData} from "../../interfaces/store/actions/payload/home";
import {CLICK_EVENT, DrawerTabs, ENTER_KEY} from "../../utils/constants";
import JobEmpty from "../../components/JobEmpty/JobEmpty";

interface StateProps {
    featuredJobs: JobEntity[]
    availableJobs: JobEntity[]
    inProgressJobs: JobEntity[]
    announcements: AnnouncementData[]
    isLoading: boolean
    translate: TranslateFunction
    isMobile: boolean
}

interface DispatchProps {
    getHomeData(): GetHomeDataAction

    clearHomeData(): ClearHomeDataAction

    setActiveJobType(payload: string): SetActiveJobTypeAction

    setJobFilterType(payload: string): SetJobFilterTypeAction

    getPhaseConfigList(): GetPhaseConfigListAction

    showJobDrawer(payload: string): ShowJobDrawerAction

    setSelectedJob(payload: Nullable<JobEntity>): SetSelectedJobAction
}

type Props = StateProps & DispatchProps;

class HomeView extends React.Component<Props> {
    private static readonly translationPath: string = "pages.HomeView.HomeView";

    constructor(props: Props) {
        super(props);

        this.routeTo = this.routeTo.bind(this);
        this.jobsCounter = this.jobsCounter.bind(this);
        this.showAnnouncements = this.showAnnouncements.bind(this);
    }

    componentDidMount(): void {
        this.props.getHomeData();
        this.props.getPhaseConfigList();
    }

    componentWillUnmount(): void {
        this.props.clearHomeData();
    }

    /**
     * Depending on number featured jobs, return two or less for presentation
     * @param {JobEntity[]} featuredJobs
     * @return {React.ReactNode}
     */
    getFeaturedJobs(featuredJobs: JobEntity[]): React.ReactNode {
        const jobs = featuredJobs.slice(0, 2);
        return (
            <div className="df-home-section-featured-jobs">
                {jobs.map((job: JobEntity): React.ReactNode => (
                    <div
                        role="button"
                        tabIndex={0}
                        className="df-home-job-item-wrapper"
                        key={job.id}
                        onClick={this.redirectJobItem.bind(this, job, JobType.FEATURED)}
                        onKeyDown={this.redirectJobItem.bind(this, job, JobType.FEATURED)}
                    >
                        <JobItem data={job} hideBtnDropdown={false}/>
                    </div>
                ))}
            </div>
        );
    }

    /**
     * Based on job type, we return job item with clickable wrapper that handles redirect to open task drawer on jobs view
     * @param {JobEntity} data
     * @param {string} jobType
     * @param {boolean} hideBtnDropdown
     * @return {React.ReactNode}
     */
    getJobItem(data: JobEntity, jobType: string, hideBtnDropdown: boolean): React.ReactNode {
        return (
            <div
                role="button"
                tabIndex={0}
                className="df-home-job-item-wrapper"
                onClick={this.redirectJobItem.bind(this, data, jobType)}
                onKeyDown={this.redirectJobItem.bind(this, data, jobType)}
            >
                <JobItem data={data} hideBtnDropdown={hideBtnDropdown}/>
            </div>
        );
    }

    /**
     * Redirect user to jobs view with selected job and opened task drawer for that selected job
     * @param {JobEntity} data
     * @param {string} jobType
     * @param {React.MouseEvent | React.KeyboardEvent} event
     */
    redirectJobItem(data: JobEntity, jobType: string, event: React.MouseEvent | React.KeyboardEvent): void {
        const {key, type, currentTarget} = event as React.KeyboardEvent;
        if (currentTarget === document.activeElement && (type === CLICK_EVENT || key === ENTER_KEY)) {
            this.routeTo(jobType);
            this.props.setSelectedJob(data);
            this.props.showJobDrawer(DrawerTabs.DETAILS);
        }
    }

    /**
     * List announcements if there are any, otherwise show placeholder text
     * @param {AnnouncementData[]} data
     * @return {React.ReactNode}
     */
    showAnnouncements(data: AnnouncementData[]): React.ReactNode {
        return data.length ? data.map((current: AnnouncementData, index: number): React.ReactNode => (
            <Announcement key={index} data={current}/>
        )) : (
            <Empty
                image={<NoAnnouncementsIcon/>}
                description={<Translate id={`${HomeView.translationPath}.noAnnouncementsYet.text`}/>}
            />
        );
    }

    /**
     * Based on home job type section, we activate job filter type and and active job type,
     * after that we re-route to active job type tab on job view
     * @param {string} jobType
     */
    routeTo(jobType: string): void {
        switch (jobType) {
            case JobType.FEATURED:
            case JobType.AVAILABLE:
                this.props.setJobFilterType(JobFilterType.CLAIMABLE);
                break;
            case JobType.IN_PROGRESS:
                this.props.setJobFilterType(JobFilterType.ONGOING);
                break;
            case JobType.COMPLETED:
                this.props.setJobFilterType(JobFilterType.DONE);
                break;
            default:
                break;
        }
        this.props.setActiveJobType(jobType);
        const historyParams: Nullable<HistoryParams> = getHistoryParams();
        history.push(`/${historyParams?.idpId}/jobs`);
    }

    /**
     * Returns “view all” template or just template with the number of available jobs, if the number of jobs is 10 or more, template displays “10+”
     * @param {JobEntity[]} jobs
     * @param {string} jobType
     * @returns {string} viewAllJobs
     */
    jobsCounter(jobs: JobEntity[], jobType: string): string {
        const availableJobs: string = jobs.length < 10 ? jobs.length.toString() : "10+";
        let viewAllJobs: string = formatString(this.props.translate(`${HomeView.translationPath}.viewAll.text`) as string, availableJobs);
        if (jobType !== JobType.FEATURED && this.props.isMobile) {
            viewAllJobs = availableJobs;
        }
        return viewAllJobs;
    }

    render(): React.ReactNode {
        const {translationPath}: typeof HomeView = HomeView;
        const {
            featuredJobs,
            availableJobs,
            inProgressJobs,
            announcements,
            isLoading,
            isMobile,
        }: Props = this.props;
        return (
            <div className="df-home-view">
                {isLoading && (<Spinner size="large"/>)}
                <section className="df-home-content">
                    <div className="df-home-featured">
                        <div className="df-home-section-header">
                            <h2><Translate id={`${translationPath}.featuredTitle.text`}/></h2>
                            {!!featuredJobs.length && (
                                <Button
                                    type="link"
                                    onClick={this.routeTo.bind(this, JobType.FEATURED)}
                                >
                                    {this.jobsCounter(featuredJobs, JobType.FEATURED)}
                                </Button>
                            )}
                        </div>
                        {!isLoading && (featuredJobs.length ? this.getFeaturedJobs(featuredJobs) : <JobEmpty jobType={JobType.FEATURED}/>)}
                    </div>
                    <div className="df-home-content-bottom">
                        {isMobile ? (
                            <>
                                <h2><Translate id={`${translationPath}.jobs.text`}/></h2>
                                <div className="df-jobs-mobile-container">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className="df-in-progress-jobs-mobile"
                                        onClick={this.routeTo.bind(this, JobType.IN_PROGRESS)}
                                    >
                                        <span>{this.jobsCounter(inProgressJobs, JobType.IN_PROGRESS)}</span>
                                        <div><Translate id={`${translationPath}.inProgressTitleMobile.text`}/></div>
                                    </div>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className="df-available-jobs-mobile"
                                        onClick={this.routeTo.bind(this, JobType.AVAILABLE)}
                                    >
                                        <span>{this.jobsCounter(availableJobs, JobType.AVAILABLE)}</span>
                                        <div><Translate id={`${translationPath}.availableTitleMobile.text`}/></div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="df-home-available-jobs">
                                    <div className="df-home-section-header">
                                        <h2><Translate id={`${translationPath}.availableTitle.text`}/></h2>
                                        {!!availableJobs.length && (
                                            <Button
                                                type="link"
                                                onClick={this.routeTo.bind(this, JobType.AVAILABLE)}
                                            >
                                                {this.jobsCounter(availableJobs, JobType.AVAILABLE)}
                                            </Button>
                                        )}
                                    </div>
                                    {!isLoading
                                    && (availableJobs.length
                                        ? this.getJobItem(availableJobs[0], JobType.AVAILABLE, false)
                                        : <JobEmpty jobType={JobType.AVAILABLE}/>)}
                                </div>
                                <div className="df-home-in-progress-jobs">
                                    <div className="df-home-section-header">
                                        <h2><Translate id={`${translationPath}.inProgressTitle.text`}/></h2>
                                        {!!inProgressJobs.length && (
                                            <Button
                                                type="link"
                                                onClick={this.routeTo.bind(this, JobType.IN_PROGRESS)}
                                            >
                                                {this.jobsCounter(inProgressJobs, JobType.IN_PROGRESS)}
                                            </Button>
                                        )}
                                    </div>
                                    {!isLoading && (inProgressJobs.length
                                        ? this.getJobItem(inProgressJobs[0], JobType.IN_PROGRESS, true)
                                        : <JobEmpty jobType={JobType.IN_PROGRESS}/>)}
                                </div>
                            </>
                        )}
                    </div>
                </section>
                <aside className="df-home-aside">
                    <div className="df-home-announcements">
                        <h2><Translate id={`${translationPath}.announcementsTitle.text`}/></h2>
                        <div className="df-home-announcements-container">
                            {!isLoading && this.showAnnouncements(announcements)}
                        </div>
                    </div>
                </aside>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        featuredJobs: state.home.featuredJobs,
        availableJobs: state.home.availableJobs,
        inProgressJobs: state.home.inProgressJobs,
        announcements: state.home.announcements,
        isLoading: state.mask.isLoading,
        translate: getTranslate(state.localize),
        isMobile: state.mobile.isMobile,
    };
}

const mapDispatchToProps: DispatchProps = {
    getHomeData,
    clearHomeData,
    setActiveJobType,
    setJobFilterType,
    getPhaseConfigList,
    showJobDrawer,
    setSelectedJob,
};

export default connect(mapStateToProps, mapDispatchToProps)(HomeView);
